import React, { useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { FormControlInput } from "../../../../components/organisms/Form/control";
import { ButtonT1 } from "../../../../components/atoms/Button/Button";
import { ManageGeneralFormProvider } from "../../../../components/organisms/Provider/ManageGeneralFormProvider";
import { FormCard, FormTitle, FormSubtitle, BackLink } from "../index.styles";
import { usePortalAcceptInviteMutation } from "../../../../store/apiSlice/portalAuthApiSlice";
import { useNotification } from "../../../../hooks/useNotification";

const AcceptInvite = () => {
  const notify = useNotification();
  const [submitted, setSubmitted] = useState(false);

  const { subdomain } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [acceptInvite, { isLoading }] = usePortalAcceptInviteMutation();

  const formik = useFormik({
    initialValues: {
      password: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      password: Yup.string()
        .min(8, "Password must be at least 8 characters")
        .required("Password is required"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("password"), null], "Passwords do not match")
        .required("Confirm password is required"),
    }),
    onSubmit: async (values, { setErrors }) => {
      try {
        await acceptInvite({
          token,
          password: values.password,
          password_confirm: values.confirmPassword,
        }).unwrap();
        setSubmitted(true);
      } catch (err) {
        const error = err.data;
        notify({
          type: "error",
          message: error?.error || "Failed to accept invitation. Please try again.",
        });
      }
    },
  });

  if (!token) {
    return (
      <FormCard>
        <FormTitle>Invalid Invitation Link</FormTitle>
        <FormSubtitle>This invitation link is invalid or has expired.</FormSubtitle>
      </FormCard>
    );
  }

  if (submitted) {
    return (
      <FormCard>
        <FormTitle>Account Created Successfully</FormTitle>
        <FormSubtitle>Your account has been created.</FormSubtitle>
        <BackLink onClick={() => navigate(`/portal/${subdomain}/auth/login`)}>
          Go to Sign in
        </BackLink>
      </FormCard>
    );
  }

  return (
    <FormCard>
      <FormTitle>Set up your account</FormTitle>
      <FormSubtitle>Set a password for your account to accept the invitation.</FormSubtitle>
      <ManageGeneralFormProvider formik={formik}>
        <form onSubmit={formik.handleSubmit}>
          <div className="mb-7">
            <FormControlInput
              name="password"
              type="password"
              label="Password"
              placeholder="Password"
              variant="light"
              context="general"
              required
            />
          </div>

          <div className="mb-7">
            <FormControlInput
              name="confirmPassword"
              type="password"
              label="Confirm Password"
              placeholder="Confirm Password"
              variant="light"
              context="general"
              required
            />
          </div>

          <ButtonT1
            title={isLoading ? "Setting up..." : "Create Account"}
            loading={isLoading}
            disabled={isLoading}
            type="submit"
            $variant="primary"
            $width="100%"
          />
        </form>
      </ManageGeneralFormProvider>
    </FormCard>
  );
};

export default AcceptInvite;
